import { useState, type FormEvent } from 'react';
import { api, EchecApi } from '../partage/api/client';
import { AccueilSansCompte, ChampEmail, ChampMotDePasse } from './AccueilSansCompte';
import './Connexion.css';

/**
 * D1 · La connexion
 *
 * Un e-mail et un mot de passe (chantier comptes, ticket 02). Le champ e-mail reprend la ligne
 * du mot de passe, sans l'espacement des points. Sous « Entrer », « Mot de passe oublié ? »
 * (chantier mails) ; plus bas, le lien vers l'inscription (ticket 03). Un compte neuf qui n'a pas
 * encore ouvert le lien de son mail est mené à « Regarde ta boîte mail ».
 */
export function Connexion({
  onEntre,
  onCreerUnCompte,
  onOubli,
  onAVerifier,
}: {
  onEntre: () => void;
  onCreerUnCompte: () => void;
  onOubli: () => void;
  onAVerifier: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const pret = email.trim() !== '' && motDePasse !== '';

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    if (!pret || enCours) return;

    setEnCours(true);
    setRefus(null);
    try {
      await api.connexion(email, motDePasse);
      onEntre();
    } catch (erreur) {
      if (erreur instanceof EchecApi && erreur.code === 'adresse_non_verifiee') {
        onAVerifier(email);
        return;
      }
      setRefus(messageDeRefus(erreur));
      setMotDePasse('');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <AccueilSansCompte titre="Entrer" description="Tes projets sont à toi seul : aucun autre compte ne les voit.">
      <form className="connexion-carte" onSubmit={soumettre}>
        <label className="connexion-etiquette" htmlFor="email">
          E-mail
        </label>
        <ChampEmail id="email" valeur={email} onChange={setEmail} />

        <label className="connexion-etiquette" htmlFor="mot-de-passe">
          Mot de passe
        </label>
        <ChampMotDePasse
          id="mot-de-passe"
          valeur={motDePasse}
          onChange={setMotDePasse}
          autoComplete="current-password"
        />

        <button className="connexion-entrer" type="submit" disabled={!pret || enCours}>
          {enCours ? 'Un instant' : 'Entrer'}
        </button>

        {refus && (
          <p className="refus connexion-refus" role="status">
            {refus}
          </p>
        )}
      </form>

      <button type="button" className="connexion-lien" onClick={onOubli}>
        Mot de passe oublié&nbsp;?
      </button>
      <p className="connexion-separation">Pas encore de carnet&nbsp;?</p>
      <button type="button" className="connexion-second" onClick={onCreerUnCompte}>
        Créer mon compte
      </button>
      <p className="connexion-aide">Il te faut un code d&apos;invitation.</p>
    </AccueilSansCompte>
  );
}

function messageDeRefus(erreur: unknown): string {
  if (!(erreur instanceof EchecApi)) return 'Quelque chose n’a pas répondu. Réessaie.';
  switch (erreur.code) {
    case 'identifiants_refuses':
      return 'E-mail ou mot de passe incorrect.';
    case 'hors_ligne':
      return 'Pas de réseau. La connexion est le seul geste qui en a besoin.';
    default:
      return erreur.statut === 429
        ? 'Trop de tentatives. Laisse passer quelques minutes.'
        : 'Quelque chose n’a pas répondu. Réessaie.';
  }
}
